import {
  EDITOR_HISTORY_REDO,
  EDITOR_HISTORY_RESET,
  EDITOR_HISTORY_STATE,
  EDITOR_HISTORY_UNDO,
  PROJECTJSON,
} from './constants/projectinfo';

const HISTORY_LIMIT = 50;

const cloneProject = (project) => {
  if (typeof structuredClone === 'function') return structuredClone(project);
  return JSON.parse(JSON.stringify(project));
};

const hasProject = (project) => project && Object.keys(project).length > 0;

export const createEditorHistoryMiddleware = (limit = HISTORY_LIMIT) => {
  let past = [];
  let future = [];
  let lastMergeKey = null;
  let lastRecordedAt = 0;

  return (store) => (next) => (action) => {
    const publishState = (lastAction = null) => {
      store.dispatch({
        type: EDITOR_HISTORY_STATE,
        payload: {
          undoCount: past.length,
          redoCount: future.length,
          lastAction,
        },
      });
    };

    if (action.type === EDITOR_HISTORY_RESET) {
      past = [];
      future = [];
      lastMergeKey = null;
      lastRecordedAt = 0;
      const result = next(action);
      publishState();
      return result;
    }

    if (action.type === EDITOR_HISTORY_UNDO) {
      if (!past.length) return action;
      const current = store.getState().projectdata.projectJSON;
      const entry = past.pop();
      future.push({ snapshot: cloneProject(current), label: entry.label });
      lastMergeKey = null;
      lastRecordedAt = 0;
      next({ type: PROJECTJSON, payload: entry.snapshot, meta: { history: { record: false } } });
      publishState(entry.label);
      return action;
    }

    if (action.type === EDITOR_HISTORY_REDO) {
      if (!future.length) return action;
      const current = store.getState().projectdata.projectJSON;
      const entry = future.pop();
      past.push({ snapshot: cloneProject(current), label: entry.label });
      lastMergeKey = null;
      lastRecordedAt = 0;
      next({ type: PROJECTJSON, payload: entry.snapshot, meta: { history: { record: false } } });
      publishState(entry.label);
      return action;
    }

    if (action.type !== PROJECTJSON) return next(action);

    const history = action.meta?.history || {};
    if (history.reset) {
      past = [];
      future = [];
      lastMergeKey = null;
      lastRecordedAt = 0;
      const result = next(action);
      publishState();
      return result;
    }

    const previous = store.getState().projectdata.projectJSON;
    const shouldRecord = history.record !== false && hasProject(previous) && previous !== action.payload;
    if (shouldRecord) {
      const mergeKey = history.mergeKey || null;
      const now = Date.now();
      if (!mergeKey || mergeKey !== lastMergeKey || now - lastRecordedAt > 750) {
        past.push({
          snapshot: cloneProject(previous),
          label: history.label || 'Edit project',
        });
        if (past.length > limit) past.shift();
      }
      future = [];
      lastMergeKey = mergeKey;
      lastRecordedAt = now;
    } else {
      lastMergeKey = null;
      lastRecordedAt = 0;
    }

    const result = next(action);
    if (shouldRecord) publishState(history.label || 'Edit project');
    return result;
  };
};
